import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Calendar,
  Clock,
  Users,
  CheckCircle,
  ArrowRight,
  Play,
  Star,
  Shield,
  Zap,
  Target,
  MessageSquare,
  Building,
  Phone,
  Mail,
  MapPin,
} from "lucide-react";
import RequestDemoModal from "@/components/modals/RequestDemoModal";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { SEO } from "@/components/SEO";

const RequestDemo = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const features = [
    {
      icon: <Play className="h-8 w-8 text-blue-600" />,
      title: "Demo Personalizada",
      description:
        "Demostración adaptada a tu práctica legal específica y casos reales",
      color: "bg-blue-50 border-blue-200",
    },
    {
      icon: <Users className="h-8 w-8 text-green-600" />,
      title: "Experto Dedicado",
      description:
        "Un especialista en IA legal te guiará paso a paso durante toda la sesión",
      color: "bg-green-50 border-green-200",
    },
    {
      icon: <Clock className="h-8 w-8 text-purple-600" />,
      title: "30 Minutos Efectivos",
      description:
        "Sesión concisa pero completa para conocer todas las funcionalidades",
      color: "bg-purple-50 border-purple-200",
    },
    {
      icon: <Shield className="h-8 w-8 text-orange-600" />,
      title: "Sin Compromiso",
      description:
        "Explora sin presión, decide después de conocer todas las opciones",
      color: "bg-orange-50 border-orange-200",
    },
  ];

  const benefits = [
    {
      icon: <Target className="h-6 w-6 text-blue-500" />,
      title: "Análisis de Casos Reales",
      description: "Trabajaremos con casos específicos de tu práctica legal",
    },
    {
      icon: <Zap className="h-6 w-6 text-yellow-500" />,
      title: "Demostración Completa",
      description: "Todas las funcionalidades de IA en acción",
    },
    {
      icon: <MessageSquare className="h-6 w-6 text-green-500" />,
      title: "Respuestas Técnicas",
      description: "Resolvemos todas tus dudas sobre implementación",
    },
    {
      icon: <CheckCircle className="h-6 w-6 text-purple-500" />,
      title: "Estrategias Personalizadas",
      description: "Plan de implementación adaptado a tu firma",
    },
    {
      icon: <Building className="h-6 w-6 text-indigo-500" />,
      title: "Precios Transparentes",
      description: "Plan de precios personalizado según tu volumen",
    },
    {
      icon: <Users className="h-6 w-6 text-pink-500" />,
      title: "Soporte Continuo",
      description: "Acompañamiento durante toda la implementación",
    },
  ];

  const testimonials = [
    {
      name: "Dr. María González",
      role: "Socia, González & Asociados",
      content:
        "La demo me mostró exactamente cómo la IA puede optimizar mi práctica. En 30 minutos entendí el potencial completo de la plataforma.",
      rating: 5,
    },
    {
      name: "Lic. Carlos Mendoza",
      role: "Director Legal, TechCorp",
      content:
        "Increíble cómo la plataforma puede analizar contratos en segundos. La demo fue muy clara y el experto resolvió todas mis dudas.",
      rating: 5,
    },
    {
      name: "Dra. Ana Rodríguez",
      role: "Abogada Independiente",
      content:
        "La personalización de la demo fue perfecta. Mostraron exactamente las funcionalidades que necesito para mi práctica.",
      rating: 5,
    },
  ];

  const faqs = [
    {
      question: "¿Cuánto tiempo dura la demo?",
      answer:
        "La demostración tiene una duración de 30 minutos, tiempo suficiente para mostrar todas las funcionalidades principales y responder tus preguntas.",
    },
    {
      question: "¿Necesito preparar algo antes de la demo?",
      answer:
        "No es necesario preparar nada. Solo necesitamos que nos cuentes sobre tu práctica legal para personalizar la demostración.",
    },
    {
      question: "¿La demo es realmente personalizada?",
      answer:
        "Sí, adaptamos la demostración a tu tipo de práctica legal, mostrando las funcionalidades más relevantes para tu trabajo diario.",
    },
    {
      question: "¿Hay algún compromiso después de la demo?",
      answer:
        "No, la demo es completamente gratuita y sin compromiso. Puedes decidir si continuar o no después de conocer la plataforma.",
    },
    {
      question: "¿Puedo hacer preguntas durante la demo?",
      answer:
        "¡Por supuesto! De hecho, te animamos a hacer todas las preguntas que tengas. Nuestro experto estará ahí para resolverlas.",
    },
    {
      question: "¿Qué pasa si no puedo asistir a la hora programada?",
      answer:
        "No hay problema, puedes reprogramar la demo en cualquier momento. Te enviaremos un recordatorio antes de la sesión.",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Solicitar Demo - Mi Asistente Legal IA"
        description="Descubre el poder de la IA legal con una demostración personalizada. Agenda tu demo gratuita de 30 minutos con nuestros expertos."
        keywords="demo legal, demostración IA legal, consulta gratuita, asistente legal"
      />
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Descubre el Poder de
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-indigo-300">
                {" "}
                Mi Asistente Legal IA
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Agenda una demostración personalizada y transforma tu práctica
              legal con la tecnología de inteligencia artificial más avanzada
              del mercado.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                size="lg"
                className="bg-white text-blue-900 hover:bg-blue-50 text-lg px-8 py-4"
                onClick={() => setIsModalOpen(true)}>
                <Calendar className="w-5 h-5 mr-2" />
                Solicitar Demo Gratuita
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <div className="flex items-center text-blue-200">
                <Star className="w-5 h-5 text-yellow-400 mr-2" />
                <span className="text-lg">4.9/5 de nuestros clientes</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                ¿Por qué elegir nuestra demo?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Nuestra demostración personalizada te permite conocer todas las
                capacidades de nuestra plataforma de IA legal de manera práctica
                y efectiva.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <Card
                  key={index}
                  className={`${feature.color} border-2 hover:shadow-lg transition-all duration-300`}>
                  <CardContent className="p-8 text-center">
                    <div className="flex justify-center mb-6">
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      {feature.title}
                    </h3>
                    <p className="text-gray-700">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                ¿Qué incluye tu demo?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Durante la demostración personalizada, nuestros expertos te
                mostrarán cómo nuestra plataforma puede resolver los desafíos
                específicos de tu práctica legal.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="flex-shrink-0">{benefit.icon}</div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Lo que dicen nuestros clientes
              </h2>
              <p className="text-xl text-gray-600">
                Miles de profesionales legales ya han transformado su práctica
                con nuestra IA
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <Card
                  key={index}
                  className="p-6 hover:shadow-lg transition-shadow">
                  <CardContent className="p-0">
                    <div className="flex items-center mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star
                          key={i}
                          className="w-5 h-5 text-yellow-400 fill-current"
                        />
                      ))}
                    </div>
                    <blockquote className="text-gray-700 mb-4 italic">
                      "{testimonial.content}"
                    </blockquote>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {testimonial.name}
                      </p>
                      <p className="text-gray-600 text-sm">
                        {testimonial.role}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Preguntas Frecuentes
              </h2>
              <p className="text-xl text-gray-600">
                Resolvemos las dudas más comunes sobre nuestras demostraciones
              </p>
            </div>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <Card key={index} className="p-6">
                  <CardContent className="p-0">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      {faq.question}
                    </h3>
                    <p className="text-gray-600">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">
              ¿Listo para Transformar tu Práctica Legal?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Agenda tu demo gratuita de 30 minutos y descubre cómo la IA puede
              revolucionar tu trabajo diario.
            </p>
            <Button
              size="lg"
              className="bg-white text-blue-600 hover:bg-blue-50 text-lg px-8 py-4"
              onClick={() => setIsModalOpen(true)}>
              <Calendar className="w-5 h-5 mr-2" />
              Solicitar Demo Ahora
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <p className="text-blue-200 text-sm mt-4">
              Sin compromiso • Respuesta en 24 horas • Demo personalizada
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-12 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-2xl font-bold mb-6">
              ¿Necesitas más información?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="flex items-center justify-center space-x-3">
                <Mail className="w-6 h-6 text-blue-400" />
                <span>demo@miasistentelegalia.com</span>
              </div>
              <div className="flex items-center justify-center space-x-3">
                <Phone className="w-6 h-6 text-green-400" />
                <span>+1 (555) 123-4567</span>
              </div>
              <div className="flex items-center justify-center space-x-3">
                <MapPin className="w-6 h-6 text-purple-400" />
                <span>Disponible 24/7</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Modal */}
      <RequestDemoModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default RequestDemo;
